/**
 * Trading mode — display from API `execution` (config-driven).
 * No UI mode switcher. Live orders are never placed from this shell.
 * @typedef {'paper' | 'live'} TradingMode
 */

/** @type {TradingMode} */
export const DEFAULT_MODE = 'paper'

/**
 * Live dashboard section (status / preflight / funding hook) — unlocked.
 * Does NOT mean live order placement is enabled.
 */
export const LIVE_SECTION_ENABLED = true

/**
 * Live order / mutate UI — stays false until graduation checklist.
 * Dashboard never POSTs CLOB orders.
 */
export const LIVE_ORDERS_UI_ENABLED = false

/** @deprecated use LIVE_ORDERS_UI_ENABLED — was gating the whole Live tab */
export const LIVE_ENABLED = LIVE_ORDERS_UI_ENABLED
/** @deprecated alias */
export const LIVE_UI_READY = LIVE_ORDERS_UI_ENABLED

export const LIVE_GRADUATION_TOOLTIP =
  'Ордера live — после graduation checklist. Сейчас: только статус и префлайт.'
/** @deprecated alias */
export const LIVE_DISABLED_TITLE = LIVE_GRADUATION_TOOLTIP

export const MODE_LABELS = {
  paper: 'Бумага',
  live: 'Живая',
}

/** Header / Hub badge copy (no emoji — color carries meaning). */
export const MODE_BADGE = {
  paper: 'БУМАГА',
  live: 'ЖИВАЯ',
}

export const BANKROLL_SUFFIX = {
  paper: '(бумага)',
  live: 'pUSD (реальный)',
}

/**
 * @param {unknown} raw
 * @returns {TradingMode}
 */
export function normalizeMode(raw) {
  const m = String(raw || '')
    .trim()
    .toLowerCase()
  return m === 'live' ? 'live' : 'paper'
}

export function modeLabel(mode) {
  return MODE_LABELS[mode] || mode
}

export function modeBadgeText(mode) {
  return MODE_BADGE[normalizeMode(mode)] || MODE_BADGE.paper
}

export function bankrollSuffix(mode) {
  return BANKROLL_SUFFIX[normalizeMode(mode)] || BANKROLL_SUFFIX.paper
}

/** Unit name for ops mutate — paper 15m or live 15m. */
export function opsUnitForMode(mode) {
  if (normalizeMode(mode) === 'live') return 'pmbot-live-15m'
  return 'pmbot-paper'
}

/** Both paper systemd units (15m + 5m + ETH multi-coin). */
export const PAPER_OPS_UNITS = [
  'pmbot-paper',
  'pmbot-paper-5m',
  'pmbot-paper-eth',
  'pmbot-paper-eth-5m',
]

/** Live production units (ETH 15m only). */
export const LIVE_OPS_UNITS = ['pmbot-live-15m']

/** True when ops mutate skips typed confirm (SSO-only, like AI CES toggle). */
export function isLiveOpsUnit(unit) {
  return LIVE_OPS_UNITS.includes(String(unit || ''))
}

/**
 * Normalize `execution` block from /api/paper or /api/ops/status.
 * @param {Record<string, unknown> | null | undefined} raw
 */
export function normalizeExecution(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      execution_mode: DEFAULT_MODE,
      live_confirm: false,
      live_confirm_env: 'LIVE_CONFIRM',
      live_confirm_required: false,
      kill_switch_active: false,
      kill_switch_path: null,
      live_section_ready: LIVE_SECTION_ENABLED,
      live_ui_ready: false,
      live_orders_armed: false,
      live_15m_unit_active: null,
      paper_5m_only: true,
      min_bankroll_floor: null,
      max_daily_loss_abs: null,
      max_stake_abs: null,
      smoke_assumed_bankroll: null,
      onchain_pusd_balance: null,
      onchain_pusd_ok: null,
      onchain_pusd_rpc: null,
      onchain_pusd_source: null,
      onchain_pusd_holder: null,
      onchain_pol_balance: null,
      onchain_pol_ok: null,
      onchain_pol_rpc: null,
      onchain_pol_min: 0.5,
      mos: null,
      wallet_address: null,
      wallet_source: null,
      signer_configured: true,
      l2_creds_present: true,
      funding: {
        status: 'onchain_ready',
        label_ru: 'Пополнение кошелька',
        note_ru:
          'On-chain READY: wrap + approve Exchange сделаны. Deposit UI в RU не нужен.',
        steps_ru: [],
        unclear_ru: [],
        safe_now_ru: '',
        report: 'docs/live-funding-gio-reverify-2026-07-28.md',
        wrap_path_done: true,
        deposit_ui_blocked_ru: true,
      },
      docs: {
        preflight: 'docs/live-preflight.md',
        smoke: 'docs/live-smoke-tiny.md',
        funding_reverify: 'docs/live-funding-gio-reverify-2026-07-28.md',
      },
      connection: { paper_bot: null, live_15m: null, clob: null },
      recent_attempts: [],
      live_bets: {
        bets: [],
        bet_count: null,
        pending_count: null,
        open_stake: null,
        wins: null,
        losses: null,
        win_rate: null,
        wins_today: null,
        losses_today: null,
        win_rate_today: null,
        wins_all: null,
        losses_all: null,
        win_rate_all: null,
        source: 'empty',
      },
      smoke: {
        label_ru: 'LIVE 15m',
        status: 'waiting_georg_ok',
        waiting_ru: 'Ждём unit pmbot-live-15m.',
        note_ru: '',
        parity_ru: '',
        funded_note_ru: '',
      },
      safety_ru:
        'Ордера с дашборда не отправляются. Реальный POST /order только у pmbot-live-15m.',
      preflight: {
        wallet_ok: true,
        onchain_ready: true,
        wrap_ok: true,
        pusd_ok: true,
        pol_ok: true,
        approve_ok: true,
        signer_configured: true,
        l2_creds_present: true,
        live_confirm_ok: false,
        kill_clear: true,
        mode_paper: true,
        live_15m_unit_active: null,
        paper_5m_only: true,
        floor_configured: false,
        caps_configured: false,
        assumed_bankroll_configured: false,
        onchain_source: 'operator_confirmed',
      },
    }
  }
  const mode = normalizeMode(raw.execution_mode)
  const liveConfirm = !!raw.live_confirm
  const kill = !!raw.kill_switch_active
  const pre = raw.preflight && typeof raw.preflight === 'object' ? raw.preflight : {}
  const funding = raw.funding && typeof raw.funding === 'object' ? raw.funding : {}
  const docs = raw.docs && typeof raw.docs === 'object' ? raw.docs : {}
  const connection = raw.connection && typeof raw.connection === 'object' ? raw.connection : {}
  const tri = (v) => (v === true ? true : v === false ? false : null)
  const numOrNull = (v) => {
    if (v == null || v === '') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  const attempts = Array.isArray(raw.recent_attempts) ? raw.recent_attempts : []
  const liveBetsRaw = raw.live_bets && typeof raw.live_bets === 'object' ? raw.live_bets : {}
  const liveBetsList = Array.isArray(liveBetsRaw.bets) ? liveBetsRaw.bets : []
  const signerOk = tri(raw.signer_configured ?? pre.signer_configured)
  const l2Ok = tri(raw.l2_creds_present ?? pre.l2_creds_present)
  return {
    execution_mode: mode,
    live_confirm: liveConfirm,
    live_confirm_env: String(raw.live_confirm_env || 'LIVE_CONFIRM'),
    live_confirm_required: !!(raw.live_confirm_required ?? (mode === 'live' && !liveConfirm)),
    kill_switch_active: kill,
    kill_switch_path: raw.kill_switch_path != null ? String(raw.kill_switch_path) : null,
    live_section_ready: raw.live_section_ready !== false,
    live_ui_ready: !!raw.live_ui_ready,
    live_orders_armed: !!raw.live_orders_armed,
    live_15m_unit_active: tri(raw.live_15m_unit_active),
    paper_5m_only: raw.paper_5m_only !== false,
    min_bankroll_floor: numOrNull(raw.min_bankroll_floor),
    max_daily_loss_abs: numOrNull(raw.max_daily_loss_abs),
    max_stake_abs: numOrNull(raw.max_stake_abs),
    smoke_assumed_bankroll: numOrNull(raw.smoke_assumed_bankroll),
    live_min_stake: numOrNull(raw.live_min_stake),
    onchain_pusd_balance: numOrNull(raw.onchain_pusd_balance),
    onchain_pusd_ok: tri(raw.onchain_pusd_ok),
    onchain_pusd_rpc: raw.onchain_pusd_rpc != null ? String(raw.onchain_pusd_rpc) : null,
    onchain_pusd_source: raw.onchain_pusd_source != null ? String(raw.onchain_pusd_source) : null,
    onchain_pusd_holder: raw.onchain_pusd_holder ? String(raw.onchain_pusd_holder) : null,
    onchain_pol_balance: numOrNull(raw.onchain_pol_balance),
    onchain_pol_ok: tri(raw.onchain_pol_ok),
    onchain_pol_rpc: raw.onchain_pol_rpc != null ? String(raw.onchain_pol_rpc) : null,
    onchain_pol_min: numOrNull(raw.onchain_pol_min) ?? 0.5,
    expected_t3_stake_usd: numOrNull(raw.expected_t3_stake_usd),
    b_day_baseline_usd: numOrNull(raw.b_day_baseline_usd),
    mos: numOrNull(raw.mos),
    wallet_address: raw.wallet_address ? String(raw.wallet_address) : null,
    wallet_source: raw.wallet_source ? String(raw.wallet_source) : null,
    signer_configured: signerOk,
    l2_creds_present: l2Ok,
    smoke: (() => {
      const s = raw.smoke && typeof raw.smoke === 'object' ? raw.smoke : {}
      return {
        label_ru: String(s.label_ru || 'LIVE 15m'),
        status: String(s.status || 'waiting_georg_ok'),
        waiting_ru: String(s.waiting_ru || 'Ждём unit pmbot-live-15m.'),
        note_ru: String(
          s.note_ru ||
            'Live 15m. Дашборд ордера не шлёт. docs/live-smoke-tiny.md',
        ),
        parity_ru: s.parity_ru != null ? String(s.parity_ru) : '',
        funded_note_ru: s.funded_note_ru != null ? String(s.funded_note_ru) : '',
        max_stake_abs: numOrNull(s.max_stake_abs ?? raw.max_stake_abs),
        max_daily_loss_abs: numOrNull(s.max_daily_loss_abs ?? raw.max_daily_loss_abs),
        min_bankroll_floor: numOrNull(s.min_bankroll_floor ?? raw.min_bankroll_floor),
        smoke_assumed_bankroll: numOrNull(
          s.smoke_assumed_bankroll ?? raw.smoke_assumed_bankroll,
        ),
        live_min_stake: numOrNull(s.live_min_stake ?? raw.live_min_stake),
        onchain_pusd_balance: numOrNull(
          s.onchain_pusd_balance ?? raw.onchain_pusd_balance,
        ),
        expected_t3_stake_usd: numOrNull(
          s.expected_t3_stake_usd ?? raw.expected_t3_stake_usd,
        ),
      }
    })(),
    funding: {
      status: String(funding.status || 'onchain_ready'),
      label_ru: String(funding.label_ru || 'Пополнение кошелька'),
      note_ru: String(
        funding.note_ru ||
          'On-chain READY: wrap + approve Exchange. Deposit UI в RU не нужен. Ждём «можно smoke».',
      ),
      steps_ru: Array.isArray(funding.steps_ru)
        ? funding.steps_ru.map((s) => String(s)).filter(Boolean)
        : [],
      unclear_ru: Array.isArray(funding.unclear_ru)
        ? funding.unclear_ru.map((s) => String(s)).filter(Boolean)
        : [],
      safe_now_ru: funding.safe_now_ru != null ? String(funding.safe_now_ru) : '',
      report: funding.report != null ? String(funding.report) : '',
      recipient_eoa: funding.recipient_eoa != null ? String(funding.recipient_eoa) : '',
      wrap_path_done: funding.wrap_path_done !== false,
      deposit_ui_blocked_ru: funding.deposit_ui_blocked_ru !== false,
      onchain_source: funding.onchain_source != null ? String(funding.onchain_source) : '',
    },
    docs: {
      preflight: String(docs.preflight || 'docs/live-preflight.md'),
      smoke: String(docs.smoke || 'docs/live-smoke-tiny.md'),
      funding_reverify: String(
        docs.funding_reverify || 'docs/live-funding-gio-reverify-2026-07-28.md',
      ),
    },
    connection: {
      paper_bot: tri(connection.paper_bot),
      live_15m: tri(connection.live_15m ?? raw.live_15m_unit_active),
      clob: tri(connection.clob),
    },
    recent_attempts: attempts.slice(0, 20),
    live_bets: {
      bets: liveBetsList.slice(0, 20),
      bet_count: numOrNull(liveBetsRaw.bet_count),
      pending_count: numOrNull(liveBetsRaw.pending_count),
      open_stake: numOrNull(liveBetsRaw.open_stake),
      wins: numOrNull(liveBetsRaw.wins),
      losses: numOrNull(liveBetsRaw.losses),
      win_rate: numOrNull(liveBetsRaw.win_rate),
      wins_today: numOrNull(liveBetsRaw.wins_today),
      losses_today: numOrNull(liveBetsRaw.losses_today),
      win_rate_today: numOrNull(liveBetsRaw.win_rate_today),
      wins_all: numOrNull(liveBetsRaw.wins_all),
      losses_all: numOrNull(liveBetsRaw.losses_all),
      win_rate_all: numOrNull(liveBetsRaw.win_rate_all),
      source: liveBetsRaw.source != null ? String(liveBetsRaw.source) : 'empty',
    },
    safety_ru:
      raw.safety_ru != null
        ? String(raw.safety_ru)
        : 'Ордера с дашборда не отправляются. Реальный POST /order только при MODE=live и LIVE_CONFIRM=yes.',
    preflight: {
      wallet_ok: tri(pre.wallet_ok),
      onchain_ready: tri(pre.onchain_ready ?? pre.pusd_ok),
      wrap_ok: tri(pre.wrap_ok ?? pre.approve_ok),
      pusd_ok: tri(pre.pusd_ok),
      pol_ok: tri(pre.pol_ok),
      approve_ok: tri(pre.approve_ok),
      onchain_source:
        pre.onchain_source != null ? String(pre.onchain_source) : 'operator_confirmed',
      signer_configured: signerOk,
      l2_creds_present: l2Ok,
      signer_source: pre.signer_source != null ? String(pre.signer_source) : '',
      l2_source: pre.l2_source != null ? String(pre.l2_source) : '',
      live_confirm_ok:
        pre.live_confirm_ok === true
          ? true
          : pre.live_confirm_ok === false
            ? false
            : liveConfirm,
      kill_clear: pre.kill_clear === false ? false : pre.kill_clear === true ? true : !kill,
      mode_paper: pre.mode_paper === false ? false : pre.mode_paper === true ? true : mode === 'paper',
      floor_configured:
        pre.floor_configured === true
          ? true
          : pre.floor_configured === false
            ? false
            : numOrNull(raw.min_bankroll_floor) != null,
      caps_configured: tri(pre.caps_configured),
      assumed_bankroll_configured: tri(pre.assumed_bankroll_configured),
    },
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} health
 * @param {TradingMode} mode
 */
export function bankrollFromHealth(health, mode = DEFAULT_MODE) {
  if (!health || typeof health !== 'object') return null
  if (normalizeMode(mode) === 'live') {
    const live = health.live_bankroll
    if (live && typeof live === 'object') return live
    // Honest empty until live ledger exists — do not pretend paper is live cash.
    return null
  }
  const bank = health.paper_bankroll
  return bank && typeof bank === 'object' ? bank : null
}

/**
 * Fee display only in live when a finite number exists.
 * @param {Record<string, unknown> | null | undefined} health
 * @param {TradingMode} mode
 * @returns {number | null}
 */
export function feeAmountFromHealth(health, mode) {
  if (normalizeMode(mode) !== 'live' || !health) return null
  const candidates = [health.today_fee, health.live_fee_sum, health.live_fee_est_sum, health.paper_fee_est_sum]
  for (const v of candidates) {
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v)
  }
  return null
}

/** Shorten 0x address for display. */
export function shortAddress(addr) {
  const s = String(addr || '')
  if (s.length < 12) return s || '—'
  return `${s.slice(0, 6)}…${s.slice(-4)}`
}
